import React, {
  useCallback,
  useEffect,
  useState,
} from 'react';
import CircularProgress from '@mui/material/CircularProgress';
import Popover from 'core/components/Popover';
import TextAreaWithActions from 'pages/Dashboard/components/TextAreaWithActions';
import Button from 'pages/Dashboard/components/Button';
import {
  useConfirmDialog,
  useNotify,
} from 'core/hooks';
import {
  useGetPatientAuthorization,
  useSavePatientAuthorization,
} from 'views/EMR/components/Authorizations/hooks';
import {
  getAuthorizationFormDefaults,
  serializeAuthorization,
} from 'views/EMR/components/Authorizations/formUtils';
import isNil from 'lodash/isNil';

type Props = {
  authorizationId: number;
  anchorEl: HTMLElement;
  onClose: () => void;
};

export default function AuthorizationNotes({ authorizationId, anchorEl, onClose }: Props) {
  const {
    data,
    isLoading,
    isError,
    refetch,
  } = useGetPatientAuthorization(authorizationId);
  const { mutateAsync,  isPending } = useSavePatientAuthorization();
  const notify = useNotify();
  const { confirm, closeConfirm } = useConfirmDialog();
  const [notes, setNotes] = useState('');

  useEffect(() => {
    setNotes(data?.notes ?? '');
  }, [data]);

  const detailsReady = !isNil(data) && !isLoading && !isError;
  const isDirty = notes !== (data?.notes ?? '');

  const handleClose = useCallback(() => {
    if (isDirty) {
      confirm({
        title: 'Discard changes?',
        confirmText: 'Keep Editing',
        cancelText: 'Close',
        disableBackdropClick: true,
        disableEscapeKeyDown: true,
        hideCloseBtn: true,
        onClose: () => {
          onClose();
          closeConfirm();
        },
        onConfirm: closeConfirm,
        content: (
          <p>
            Your note has unsaved changes. Do you want to close without saving?
          </p>
        ),
      });
      return;
    }
    onClose();
  }, [closeConfirm, confirm, isDirty, onClose]);

  const onSave = async () => {
      try {
        const values = getAuthorizationFormDefaults(data);
        await mutateAsync(serializeAuthorization({ ...values, notes }));
        notify({ variant: 'success', message: 'Authorization notes saved' });
        onClose();
      } catch(error) {
        console.error('Failed to save authorization notes', error);
      }
  };

  return (
    <Popover
      anchorEl={anchorEl}
      onClose={handleClose}
      onClick={(event) => event.stopPropagation()}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      classes={{ paper: '!w-[480px] p-4' }}
    >
      <div className="flex flex-col gap-3">
        <p className="font-semibold text-sm">Authorization Notes</p>
        {isLoading && <div className="flex justify-center p-4"><CircularProgress size={20} /></div>}
        {isError && (
          <div className="flex items-center justify-between gap-3 text-sm text-red-600">
            <span>Unable to load authorization notes.</span>
            <Button variant="outlined" onClick={() => refetch()}>Retry</Button>
          </div>
        )}
        {detailsReady && (
          <TextAreaWithActions
            id="authorization-table-notes"
            placeholder="Add Notes"
            maxRows={5}
            rows={3}
            value={notes}
            onChange={setNotes}
            trackingLabel="Authorization Notes"
            className="!max-h-[120px] pr-1"
          />
        )}
        <div className="flex justify-between items-center gap-2">
          <Button
            variant="outlined"
            className="flex-1 !max-w-[unset]"
            onClick={handleClose}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="primary"
            className="flex-1 !max-w-[unset]"
            onClick={onSave}
            disabled={!detailsReady || isPending}
          >
            {isPending ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </div>
    </Popover>
  );
}
